import { useState, type ReactNode } from "react";
import { Button, Upload, type UploadProps } from "zhihao-ui-react";
import type { File as FileEntity } from "@zhihaoscm/entity";
import { uploadFile, type UploadApi, validateUploadFile } from "./uploadAdapter";

export interface VideoProps {
    videoUrl?: string;
    videoId?: number;
    api?: UploadApi;
    disabled?: boolean;
    fileSize?: number;
    fileType?: string[];
    /** 控制预览是否显示浏览器原生视频控制栏。 */
    controls?: boolean;
    height?: string;
    width?: string;
    /** 允许调用方为视频预览容器添加页面专属样式。 */
    previewClassName?: string;
    /** 允许调用方为重新上传按钮添加页面专属样式。 */
    previewActionClassName?: string;
    onUpdateVideoUrl?: (value: string) => void;
    onUpdateData?: (value: FileEntity) => void;
    children?: ReactNode;
}

/** 视频上传及预览，上传服务和 Vue 版保持一致。 */
export default function Video({
    videoUrl = "",
    api,
    disabled = false,
    fileSize = 200,
    fileType = ["video/mp4", "video/quicktime", "video/x-matroska"],
    controls = true,
    height = "200px",
    width = "100%",
    onUpdateVideoUrl,
    onUpdateData,
    children,
    previewClassName,
    previewActionClassName
}: VideoProps) {
    const [currentUrl, setCurrentUrl] = useState(videoUrl);
    const beforeUpload: UploadProps["beforeUpload"] = file => validateUploadFile(file, fileType, fileSize, "视频");
    const httpRequest: UploadProps["httpRequest"] = async options => {
        const data = await uploadFile(options, api);
        const url = data.path || "";
        setCurrentUrl(url);
        onUpdateVideoUrl?.(url);
        onUpdateData?.(data);
        options.onSuccess(data);
    };
    return (
        <Upload
            action="#"
            accept={fileType.join(",")}
            disabled={disabled}
            showFileList={false}
            beforeUpload={beforeUpload}
            httpRequest={httpRequest}
        >
            {currentUrl ? (
                <div className={previewClassName} style={{ position: "relative", width, height }}>
                    <video
                        src={currentUrl}
                        controls={controls}
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                    <Button className={previewActionClassName} size="small" style={{ position: "absolute", right: 4, bottom: 4 }}>
                        重新上传
                    </Button>
                </div>
            ) : (
                children || <span>上传视频</span>
            )}
        </Upload>
    );
}
